import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, pw);
      const target = user.role === "super_admin" ? "/super" : user.role === "admin" ? "/admin" : "/portal";
      nav(target);
      toast.success(`Welcome back, ${user.name}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="container-tight py-24 max-w-md">
      <div className="kicker mb-3">Portal Access</div>
      <h1 className="text-5xl mb-8">Sign In</h1>
      <form onSubmit={go} className="space-y-5">
        <label className="block">
          <span className="kicker text-[10px] block mb-2">Email</span>
          <input required type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} className="fld" autoFocus autoComplete="email" />
        </label>
        <label className="block">
          <span className="kicker text-[10px] block mb-2">Password</span>
          <div className="relative">
            <input required type={showPw ? "text" : "password"} data-testid="login-password"
              value={pw} onChange={(e) => setPw(e.target.value)}
              className="fld pr-11" autoComplete="current-password" />
            <button type="button" onClick={() => setShowPw((s) => !s)}
              data-testid="login-toggle-pw" tabIndex={-1}
              aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showPw ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
            </button>
          </div>
        </label>
        <button data-testid="login-submit" disabled={busy} className="btn-gold w-full py-3 text-sm uppercase tracking-widest disabled:opacity-50">
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>

      <div className="mt-6 pt-6 border-t border-border">
        <button type="button" data-testid="login-google-btn"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/auth/callback";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full border border-border hover:border-primary transition-colors py-3 text-sm uppercase tracking-widest flex items-center justify-center gap-3">
          <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.3 0 6.3 1.1 8.7 3.3l6.5-6.5C35.2 2.6 30 .5 24 .5 14.6.5 6.6 5.9 2.7 14l7.7 6C12.3 14 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.2-.4-4.8H24v9.1h12.7c-.6 3-2.3 5.6-4.9 7.3l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.4 28.5c-.5-1.4-.7-2.9-.7-4.5s.3-3.1.7-4.5l-7.7-6C1 17 0 20.4 0 24s1 7 2.7 10.5l7.7-6z"/><path fill="#34A853" d="M24 47.5c6.5 0 11.9-2.1 15.8-5.8l-7.6-5.9c-2.1 1.4-4.8 2.3-8.2 2.3-6.3 0-11.7-4.3-13.6-10l-7.7 6C6.6 42.1 14.6 47.5 24 47.5z"/></svg>
          Sign in with Google
        </button>
        <p className="text-xs text-muted-foreground mt-3 text-center">
          Your Google email must already be linked to a Piplight account. Contact your relationship manager if it isn't.
        </p>
      </div>

      <div className="mt-10 text-xs text-muted-foreground leading-relaxed">
        Client accounts are created upon approved application. If you are a prospective investor, please <a href="/apply" className="gold hover:underline">submit an application</a>.
      </div>
      <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.75rem 1rem;font-size:.875rem;color:inherit}.fld:focus{outline:none;border-color:hsl(41 40% 54%)}`}</style>
    </div>
  );
}
